import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonChip,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';

import DatePicker from 'src/components/DatePicker';
import Operarios from 'src/services/operarios';
import OperariosRemplazos from 'src/services/operarioRemplazo';
import { fetchCierreDiarioOperarios } from 'src/services/CierreDiarioOperarios';
import { GestionErroresDetalleItem } from 'src/modules/gestionErrores/types';

import './ReemplazoRapidoModal.css';

// Modal de Reemplazo Rápido
// Objetivo: desde un error SIN-ASIGNACION ya tenemos:
// - Fecha cubre (fecha del error)
// - Punto de Servicio (PS)
// - Reemplazante (operario del registro SIN-ASIGNACION)
// - Horas (HE/HS) derivadas de la HNP
// Entonces solicitamos lo mínimo:
// - Cubre A (operario ausente)
// - Fecha de ausencia (para traer asignaciones/cortes y calcular cupo)
// Flujo:
// 1) Buscar/seleccionar ausente
// 2) Elegir fecha de ausencia
// 3) Listar asignaciones del ausente en ese día y filtrar por PS
// 4) Seleccionar asignación(es)
// 5) Calcular cupo del día (backend) y comparar contra horas requeridas
// 6) Guardar reemplazo (backend) y refrescar detalle

type Props = {
  isOpen: boolean;
  row: GestionErroresDetalleItem | null;
  onClose: () => void;
  onSuccess: () => void;
};

type OperarioOption = {
  label: string;
  value: string; // Formato que espera backend: "NOMBRE - LEGAJO"
  nombre: string;
  legajo: string;
  source: 'PS' | 'BUSQUEDA';
};

const mergeUniqueOptions = (a: OperarioOption[], b: OperarioOption[]): OperarioOption[] => {
  const seen = new Set<string>();
  const out: OperarioOption[] = [];
  const push = (x: OperarioOption) => {
    const key = String(x.value || '');
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(x);
  };
  (a || []).forEach(push);
  (b || []).forEach(push);
  return out;
};

const toISO = (date?: Date): string => {
  // Importante: el DatePicker puede entregar un Date en UTC.
  // Si usamos getters locales, en zonas horarias negativas puede correrse al día anterior.
  // Para rangos/fechas de ausencia usamos YYYY-MM-DD basado en UTC para mantener el día seleccionado.
  if (!date) return '';
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toDisplayDate = (date?: Date): string => {
  try {
    if (!date) return '';
    return date.toLocaleDateString('es-ES');
  } catch {
    return '';
  }
};

const parseAnyDate = (value?: string): Date | undefined => {
  try {
    const raw = String(value || '').trim();
    if (!raw) return undefined;
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return undefined;
    return dt;
  } catch {
    return undefined;
  }
};

const hhmmToMin = (v?: string): number => {
  try {
    if (!v) return 0;
    const [h, m] = v.split(':').map((n) => parseInt(n, 10) || 0);
    return (h * 60) + m;
  } catch {
    return 0;
  }
};

const minToHHmm = (min: number): string => {
  const safe = Math.max(0, Number.isFinite(min) ? min : 0);
  const h = String(Math.floor(safe / 60)).padStart(2, '0');
  const m = String(safe % 60).padStart(2, '0');
  return `${h}:${m}`;
};

const durationMinutes = (he?: string, hs?: string): number => {
  const a = hhmmToMin(he);
  const b = hhmmToMin(hs);
  if (!a || !b) return 0;
  // Si cruza medianoche, sumamos 24h.
  if (b <= a) return (b + (24 * 60)) - a;
  return b - a;
};

const parseHorasFromDetalle = (detalle?: string): { he?: string; hs?: string } => {
  try {
    const txt = String(detalle || '');
    const mHe = txt.match(/Hentrada=([0-9]{2}:[0-9]{2})/i);
    const mHs = txt.match(/Hsalida=([0-9]{2}:[0-9]{2})/i);
    return { he: mHe?.[1], hs: mHs?.[1] };
  } catch {
    return {};
  }
};

// Igual a ReemplazosV2: obtener ent/sal según día de semana para una asignación.
const getAssignmentTimesForDate = (a: any, fechaISO: string): { ent?: string; sal?: string } => {
  try {
    const dt = new Date(fechaISO + 'T00:00:00');
    const dow = dt.getDay(); // 0=Dom..6=Sab
    const entKey = { 0: 'dom_ent', 1: 'lun_ent', 2: 'mar_ent', 3: 'mie_ent', 4: 'jue_ent', 5: 'vie_ent', 6: 'sab_ent' }[
      dow as 0 | 1 | 2 | 3 | 4 | 5 | 6
    ] as string;
    const salKey = { 0: 'dom_sal', 1: 'lun_sal', 2: 'mar_sal', 3: 'mie_sal', 4: 'jue_sal', 5: 'vie_sal', 6: 'sab_sal' }[
      dow as 0 | 1 | 2 | 3 | 4 | 5 | 6
    ] as string;
    return { ent: a?.[entKey], sal: a?.[salKey] };
  } catch {
    return {};
  }
};

const ReemplazoRapidoModal: React.FC<Props> = ({ isOpen, row, onClose, onSuccess }) => {
  const [present] = useIonToast();

  // Búsqueda del ausente
  const [remplazado, setRemplazado] = useState<string>(''); // Formato: "NOMBRE - LEGAJO"
  const [ausenteInput, setAusenteInput] = useState<string>('');
  const [ausenteLoading, setAusenteLoading] = useState<boolean>(false);
  const [ausenteOptions, setAusenteOptions] = useState<OperarioOption[]>([]);
  const [ausenteSelected, setAusenteSelected] = useState<OperarioOption | null>(null);

  // Operarios del PS en el día (para selección rápida al abrir)
  const [psOpsLoading, setPsOpsLoading] = useState<boolean>(false);
  const [psOpsOptions, setPsOpsOptions] = useState<OperarioOption[]>([]);
  const [psOpsCacheKey, setPsOpsCacheKey] = useState<string>('');
  const [ausenteOpen, setAusenteOpen] = useState<boolean>(false);

  // Fecha de ausencia
  const [fechaAusencia, setFechaAusencia] = useState<Date | undefined>(undefined);

  // Asignaciones del ausente (filtradas por PS)
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(false);
  const [asignaciones, setAsignaciones] = useState<any[]>([]);
  const [idsSeleccionados, setIdsSeleccionados] = useState<number[]>([]);

  // Cupo calculado
  const [cupoDisponible, setCupoDisponible] = useState<string>('00:00');
  const [loadingCupo, setLoadingCupo] = useState(false);

  // Guardado
  const [saving, setSaving] = useState(false);

  const psNombre = String(row?.ps_nombre || '');
  const psId = Number(row?.ps_id);
  const fechaCubreDate = useMemo(() => parseAnyDate(row?.fecha_hora), [row?.fecha_hora]);
  const fechaCubreISO = useMemo(() => toISO(fechaCubreDate), [fechaCubreDate]);
  const fechaPsISO = useMemo(() => {
    // Por defecto usamos la fecha de ausencia si está seleccionada; si no, la fecha cubre.
    const a = toISO(fechaAusencia);
    return a || fechaCubreISO;
  }, [fechaAusencia, fechaCubreISO]);

  const reemplazante = useMemo(() => {
    if (!row) return '';
    const nom = String(row.operario_nombre || '').trim();
    const leg = String(row.operario_legajo ?? '').trim();
    return nom && leg ? `${nom} - ${leg}` : '';
  }, [row]);

  const { he, hs } = useMemo(() => parseHorasFromDetalle(row?.detalle), [row?.detalle]);
  const requeridosMin = useMemo(() => durationMinutes(he, hs), [he, hs]);
  const horasRequeridas = useMemo(() => minToHHmm(requeridosMin), [requeridosMin]);

  // Reset al abrir
  useEffect(() => {
    if (!isOpen) return;

    setRemplazado('');
    setAusenteInput('');
    setAusenteOptions([]);
    setAusenteSelected(null);
    setAusenteLoading(false);

    setPsOpsLoading(false);
    setPsOpsOptions([]);
    setPsOpsCacheKey('');
    setAusenteOpen(false);

    setAsignaciones([]);
    setIdsSeleccionados([]);

    setCupoDisponible('00:00');

    // Default: ausencia = fecha cubre (habitual en SIN-ASIGNACION)
    setFechaAusencia(fechaCubreDate);

    setSaving(false);
  }, [isOpen, fechaCubreDate]);

  // Precargar lista de operarios asignados del PS/día cuando el Autocomplete se abre con input vacío.
  useEffect(() => {
    const cargar = async () => {
      if (!isOpen) return;
      if (!ausenteOpen) return;
      if (String(ausenteInput || '').trim().length > 0) return;
      if (!psId || Number.isNaN(psId) || psId <= 0) return;
      if (!fechaPsISO) return;

      const key = `${psId}|${fechaPsISO}`;
      if (psOpsCacheKey === key && psOpsOptions.length > 0) return;

      try {
        setPsOpsLoading(true);
        setPsOpsCacheKey(key);
        const data = await fetchCierreDiarioOperarios(psId, fechaPsISO, { modo: 'todos' });
        const lista = Array.isArray(data?.operarios) ? data.operarios : [];
        const opts = lista
          .map((o: any) => {
            const nombre = String(o?.operario || '').trim();
            const legajo = String(o?.legajo ?? '').trim();
            const value = nombre && legajo ? `${nombre} - ${legajo}` : '';
            const label = nombre && legajo ? `${nombre} (${legajo})` : nombre || legajo;
            return { label, value, nombre, legajo, source: 'PS' } as OperarioOption;
          })
          .filter((x: OperarioOption) => !!x.value);
        setPsOpsOptions(opts);
      } catch {
        // No bloqueamos el flujo: si falla, el usuario igual puede buscar por nombre/legajo.
        setPsOpsOptions([]);
      } finally {
        setPsOpsLoading(false);
      }
    };

    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ausenteOpen, ausenteInput, psId, fechaPsISO, isOpen]);

  // Autocomplete: buscar mientras se escribe (sin botón)
  useEffect(() => {
    if (!isOpen) return;
    if (ausenteSelected) return; // si ya hay seleccionado, no volvemos a buscar

    const q = String(ausenteInput || '').trim();
    const esLegajo = /^\d+$/.test(q);
    const minChars = esLegajo ? 2 : 3;
    if (q.length < minChars) {
      setAusenteOptions([]);
      setAusenteLoading(false);
      return;
    }

    let alive = true;
    setAusenteLoading(true);
    const t = window.setTimeout(async () => {
      try {
        // Usamos operarios-supervisores para incluir cargos SUPERVISOR como ausentes/reemplazantes.
        // El backend acepta un unico parametro q y discrimina por legajo (digitos) o nombre internamente.
        const lista = await Operarios.listarOperariosSupervisores(q);
        if (!alive) return;
        const opts = (lista || []).slice(0, 20).map((o: any) => {
          const nombre = String(o.nombre || '').trim();
          const legajo = String(o.legajo ?? '').trim();
          const value = nombre && legajo ? `${nombre} - ${legajo}` : '';
          const label = nombre && legajo ? `${nombre} (${legajo})` : nombre || legajo;
          return { label, value, nombre, legajo, source: 'BUSQUEDA' } as OperarioOption;
        }).filter((x: OperarioOption) => !!x.value);

        setAusenteOptions(opts);
      } catch {
        if (!alive) return;
        setAusenteOptions([]);
      } finally {
        if (!alive) return;
        setAusenteLoading(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [ausenteInput, ausenteSelected, isOpen]);

  const optionsFinales = useMemo(() => {
    const input = String(ausenteInput || '').trim();
    // Al abrir con input vacío: mostrar operarios del PS/día.
    if (!input) return psOpsOptions;
    // Si hay texto: mostrar resultados de búsqueda, y además los del PS primero (dedupe).
    return mergeUniqueOptions(psOpsOptions, ausenteOptions);
  }, [ausenteInput, psOpsOptions, ausenteOptions]);

  const cargarAsignaciones = async (args: { remplazadoSel: string; fechaAusISO: string }) => {
    if (!row) return;

    try {
      setLoadingAsignaciones(true);
      setAsignaciones([]);
      setIdsSeleccionados([]);

      // Backend: /remplazo/asignaciones espera "remplazado" y rango fechaInicio/fechaFin.
      const res = (await OperariosRemplazos.listarOperariosRemplazo({
        remplazado: args.remplazadoSel,
        fechaInicio: args.fechaAusISO,
        fechaFin: args.fechaAusISO,
      })) as any;

      const lista = Array.isArray(res?.data) ? res.data : [];

      // Reemplazo rápido: el PS destino viene definido por el error, filtramos por el mismo.
      const filtradas = lista.filter((a: any) => String(a?.NombrePServicio || '') === psNombre);
      setAsignaciones(filtradas);

      if (!filtradas.length) {
        present({
          message: 'El ausente no tiene asignaciones en ese PS para esa fecha.',
          duration: 3200,
          color: 'warning',
        });
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudieron cargar asignaciones del ausente.';
      present({ message: String(msg), duration: 3500, color: 'danger' });
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  // Cada vez que cambia ausente o fecha de ausencia, recargamos asignaciones.
  useEffect(() => {
    if (!isOpen) return;
    if (!remplazado || !fechaAusencia || !psNombre) return;

    const fechaAusISO = toISO(fechaAusencia);
    if (!fechaAusISO) return;

    cargarAsignaciones({ remplazadoSel: remplazado, fechaAusISO });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remplazado, fechaAusencia, psNombre, isOpen]);

  const toggleAsignacion = (id: number) => {
    setIdsSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Calcular cupo del día en base a asignaciones elegidas.
  useEffect(() => {
    const run = async () => {
      const fechaAusISO = toISO(fechaAusencia);
      if (!isOpen || !remplazado || !fechaAusISO || idsSeleccionados.length === 0) {
        setCupoDisponible('00:00');
        return;
      }
      try {
        setLoadingCupo(true);
        const res = await (OperariosRemplazos as any).cupoPorDia({
          asignaciones_id: idsSeleccionados,
          fecha_inicio: fechaAusISO,
          fecha_fin: fechaAusISO,
          ps_nombre: psNombre,
          remplazado,
          // Cupo real = cupo teórico del ausente - reemplazos ya cargados - HorasProcesadas del ausente (por PS + asignación + día)
          modo_cupo: 'real_hp',
        });
        const cupo = String(res?.data?.dias?.[0]?.cupo_total || '00:00');
        setCupoDisponible(cupo);
      } catch {
        setCupoDisponible('00:00');
      } finally {
        setLoadingCupo(false);
      }
    };
    run();
  }, [idsSeleccionados, fechaAusencia, remplazado, psNombre, isOpen]);

  const cupoMin = useMemo(() => hhmmToMin(cupoDisponible), [cupoDisponible]);
  const cupoSuficiente = useMemo(() => {
    if (requeridosMin <= 0) return true;
    return cupoMin >= requeridosMin;
  }, [cupoMin, requeridosMin]);

  const puedeGuardar = useMemo(() => {
    if (!row) return false;
    if (!psNombre || !fechaCubreISO) return false;
    if (!remplazado) return false;
    if (!reemplazante) return false;
    if (!he || !hs) return false;
    if (!idsSeleccionados.length) return false;
    // Bloqueo preventivo: si el cupo es insuficiente, evitamos el guardado.
    if (!cupoSuficiente) return false;
    return true;
  }, [row, psNombre, fechaCubreISO, remplazado, reemplazante, he, hs, idsSeleccionados.length, cupoSuficiente]);

  const guardar = async () => {
    if (!row) return;
    if (saving) return;

    if (!puedeGuardar) {
      const msg = !cupoSuficiente
        ? 'Cupo insuficiente para el horario seleccionado.'
        : 'Completá ausente, fecha y asignación(es) antes de guardar.';
      present({ message: msg, duration: 3200, color: 'warning' });
      return;
    }

    // Payload compatible con el backend de reemplazos.
    // - fechaInicioRemplazante/fechaFinRemplazante: rango de COBERTURA
    // - remplazado: AUSENTE (seleccionado)
    // - remplazante: el operario que quedó SIN-ASIGNACION
    // - asignaciones_id: asignaciones del ausente (para calcular cupo)
    // - motivo: obligatorio en backend
    const payload = {
      remplazado,
      fechaInicioRemplazante: fechaCubreISO,
      fechaFinRemplazante: fechaCubreISO,
      puntoServicio: psNombre,
      asignaciones_id: idsSeleccionados,
      motivo: 'controlMarcacion',
      remplazante: reemplazante,
      horaEntradaRemplazante: he,
      horaSalidaRemplazante: hs,
      fechas: [fechaCubreISO],
    };

    try {
      setSaving(true);

      // 1) Validar duplicado para evitar cargas repetidas.
      const dup = await (OperariosRemplazos as any).existeDuplicado(payload);
      if (dup?.data?.exists) {
        present({ message: 'Ya existe un reemplazo idéntico (duplicado).', duration: 3500, color: 'warning' });
        return;
      }

      // 2) Guardar reemplazo (en lote de 1).
      await (OperariosRemplazos as any).cargarRemplazo([payload]);

      present({ message: 'Reemplazo rápido cargado correctamente.', duration: 3000, color: 'success' });
      onClose();
      onSuccess();
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo guardar el reemplazo.';
      present({ message: String(msg), duration: 3500, color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} cssClass="reemplazo-rapido-modal">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Reemplazo rápido</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="reemplazo-rapido-content">
        <div className="container reemplazo-rapido-container">
          <IonList className="rr-list">
          <IonItem lines="full" className="rr-item rr-datos">
            <IonLabel>
              <div className="rr-section-title">Datos detectados</div>
              <div className="rr-muted">PS: {psNombre || '-'}</div>
              <div className="rr-muted">Fecha cubre: {toDisplayDate(fechaCubreDate) || '-'}</div>
              <div className="rr-muted">
                Horario (SIN-ASIGNACION): {he || '--:--'} - {hs || '--:--'} (Req: {horasRequeridas})
              </div>

              <div className="rr-muted">Reemplazante: {reemplazante || '-'}</div>
            </IonLabel>
          </IonItem>

          <IonItem lines="full" className="rr-item rr-section rr-block">
            <div style={{ width: '100%' }} className="rr-field">
              <Autocomplete
                open={ausenteOpen}
                onOpen={() => setAusenteOpen(true)}
                onClose={() => setAusenteOpen(false)}
                openOnFocus
                options={optionsFinales}
                value={ausenteSelected}
                inputValue={ausenteInput}
                onInputChange={(_ev, newValue) => {
                  setAusenteSelected(null);
                  setRemplazado('');
                  setAusenteInput(String(newValue || ''));
                }}
                onChange={(_ev, opt) => {
                  const sel = (opt as OperarioOption | null);
                  setAusenteSelected(sel);
                  const value = sel?.value ? String(sel.value) : '';
                  setRemplazado(value);
                }}
                loading={ausenteLoading || psOpsLoading}
                getOptionLabel={(o) => (o as OperarioOption).label}
                noOptionsText="Sin resultados"
                loadingText="Buscando..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Operario ausente"
                    placeholder="Escribí legajo o nombre"
                    helperText="Buscar por legajo o por nombre"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {(ausenteLoading || psOpsLoading) ? (
                            <CircularProgress color="inherit" size={18} style={{ marginRight: 8 }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </IonItem>

          {remplazado ? (
            <IonItem lines="full" className="rr-item">
              <IonLabel>
                <div className="rr-muted">Seleccionado</div>
                <IonChip className="rr-chip">{remplazado}</IonChip>
              </IonLabel>
              <IonButton
                fill="clear"
                color="medium"
                slot="end"
                onClick={() => {
                  setRemplazado('');
                  setAusenteSelected(null);
                  setAusenteInput('');
                  setAusenteOptions([]);
                }}
              >
                Quitar
              </IonButton>
            </IonItem>
          ) : null}

          <IonItem lines="full" className="rr-item rr-section">
            <div style={{ width: '100%' }} className="rr-section-body">
              <IonText color="medium">Fecha de ausencia (para calcular cupo y cortes)</IonText>
              <div style={{ marginTop: 10 }}>
                <DatePicker placeholder="Fecha de ausencia" value={fechaAusencia} setValue={setFechaAusencia} />
              </div>
            </div>
          </IonItem>

          <IonItem lines="full" className="rr-item rr-section">
            <IonLabel>
              <div className="rr-section-title">Asignaciones (ausente) en este PS</div>
              <div className="rr-muted">Seleccioná al menos una asignación</div>
            </IonLabel>
            {loadingAsignaciones ? <CircularProgress color="inherit" size={18} /> : null}
          </IonItem>

          {asignaciones.map((a: any) => {
            const fechaAusISO = toISO(fechaAusencia);
            const times = fechaAusISO ? getAssignmentTimesForDate(a, fechaAusISO) : undefined;
            const ent = times?.ent;
            const sal = times?.sal;
            const id = Number(a?.id);
            const disabled = !id || !ent || !sal;
            const checked = idsSeleccionados.includes(id);

            return (
              <IonItem key={String(a?.id)} disabled={disabled} className="rr-item">
                <IonCheckbox checked={checked} onIonChange={() => toggleAsignacion(id)} slot="start" />
                <IonLabel>
                  <div style={{ fontWeight: 700 }}>
                    Corte del día: {ent || '--:--'} - {sal || '--:--'}
                    {disabled ? ' (sin corte para esa fecha)' : ''}
                  </div>
                  <div className="rr-muted">Asignación #{a?.id}</div>
                </IonLabel>
              </IonItem>
            );
          })}

          <IonItem lines="full" className="rr-item rr-section">
            <IonLabel>
              <div className="rr-section-title">Cupo del día (calculado)</div>
              <div className="rr-muted">
                Disponible: {loadingCupo ? '...' : cupoDisponible} | Requerido: {horasRequeridas}
                {!cupoSuficiente ? ' (insuficiente)' : ''}
              </div>
            </IonLabel>
          </IonItem>
        </IonList>
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onClose}>Cancelar</IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton disabled={!puedeGuardar || saving} onClick={guardar}>
              {saving ? 'Guardando...' : 'Guardar reemplazo'}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export default ReemplazoRapidoModal;
